import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SiteContentContext } from '../context/SiteContentContext';
import { CardsContext } from '../context/CardsContext';
import QuestionModal from '../components/QuestionModal';
import IymcsMark, { withIymcsMark } from '../components/IymcsMark';
import { useSeo } from '../hooks/useSeo';
import '../styles/iymcs_page.css';

// iymcs фирменные иконки (из public/iymcs/)
const IIcon = ({ src, alt = '', size = 28 }) => (
  <img src={src} alt={alt} width={size} height={size} style={{ display: 'block', objectFit: 'contain' }} />
);


const TRUST_ITEMS = [
  { icon: '/iymcs/icons/trust-puzzle-white.svg',  title: 'Гибкий выбор компетенций',     text: 'Выбирайте области знаний, которые соответствуют вашим целям.' },
  { icon: '/iymcs/icons/trust-rope-white.svg',    title: 'Практическая морская подготовка', text: 'Реальные навыки для более безопасного плавания.' },
  { icon: '/iymcs/icons/trust-devices-white.svg', title: 'Студенческий портал',           text: 'Доступ к материалам, загрузка и подтверждение морской практики.' },
  { icon: '/iymcs/icons/trust-compass-white.svg', title: 'Безопасность навигации',        text: 'Ситуационная осведомлённость, понимание своего положения и навигационная безопасность — в основе подготовки.' },
];

const BENEFITS = [
  { art: '/iymcs/graphics/blue-seal-art.png',                              title: 'Профессиональная основа',           text: 'Стандарты подготовки, созданные на основе реальной морской практики. Система управления качеством по ISO 9001:2015.' },
  { art: '/iymcs/graphics/blue-steering-wheel-art.png',                    title: 'Понятные маршруты подготовки',      text: 'От базовой морской подготовки до океанской навигации. Это рекомендуемые маршруты и варианты обучения, а не жёсткая лестница обязательных ступеней.' },
  { art: '/iymcs/logo-art/Blue compass element transparent.png',            title: 'Безопасность навигации',            text: 'Применяйте МППСС/COLREGS, правила навигации и морскую практику, чтобы обеспечить безопасность экипажа, судна и других участников движения.' },
  { art: '/iymcs/logo-art/knot element transparent.png',                    title: 'Морская практика для реальных условий', text: 'Практические навыки, управление яхтой и принятие решений для условий, с которыми вы действительно столкнётесь.' },
  { art: '/iymcs/graphics/anchor-blob-art.png',                             title: 'Морская подготовка под ваши задачи', highlight: true, text: null },
];

const COURSE_ART = {
  deckcrew:     '/iymcs/graphics/deckcrew-art.png',
  master10sail: '/iymcs/graphics/master10-art.png',
  master20sail: '/iymcs/graphics/master20-art.png',
  master40sail: '/iymcs/graphics/master40-art.png',
  racecrew:     '/iymcs/graphics/racecrew-art.png',
  'radio-a1':   '/iymcs/graphics/radio-art.png',
  'radio-a1a2': '/iymcs/graphics/radio-art.png',
};

const ALL_IYMCS_COURSES = [
  {
    id: 'deckcrew',
    title: 'DeckCrew sail™',
    cardClass: 'navy',
    area: 'Базовая морская практика',
    who: 'Начинающие яхтсмены и члены экипажа',
    readiness: 'Стартовая точка для безопасных навыков на борту.',
    focus: 'Начальная морская практика и безопасность.',
    result: 'Более полезный и безопасный член экипажа.',
    related: 'Прибрежное плавание, радиобезопасность.',
    mapDesc: 'Базовая морская практика и начальные навыки.',
    inSkillMap: true, redTop: false,
  },
  {
    id: 'master10sail',
    title: 'Master 10GT/12m sail™',
    cardClass: 'red',
    area: 'Прибрежное плавание',
    who: 'Прибрежные плавания и ночные переходы',
    readiness: 'Подходящий опыт или оценка школы.',
    focus: 'Прибрежная навигация и морская практика.',
    result: 'Больше уверенности при управлении судном в прибрежных водах.',
    related: 'DeckCrew, Radio A1, офшорное плавание.',
    mapDesc: 'Прибрежное плавание, включая ночные переходы.',
    inSkillMap: true, redTop: true,
  },
  {
    id: 'master20sail',
    title: 'Master 20GT/15m sail™',
    cardClass: 'navy',
    area: 'Офшорное / глубоководное плавание',
    who: 'Более длительные переходы',
    readiness: 'Опыт и морская практика должны соответствовать уровню курса.',
    focus: 'Уверенность в офшорных переходах.',
    result: 'Готовность к более длительным плаваниям.',
    related: 'Прибрежное плавание, A1+A2 radio.',
    mapDesc: 'Морские и глубоководные переходы, длительные маршруты и уверенность в дальнем плавании.',
    inSkillMap: true, redTop: false,
  },
  {
    id: 'master40sail',
    title: 'Master 40GT/20m sail™',
    cardClass: 'red',
    area: 'Управление более крупными судами',
    who: 'Повышенная ответственность и профессиональные планы',
    readiness: 'Может подойти опытным кандидатам напрямую.',
    focus: 'Управление более крупными судами и профессиональная ответственность.',
    result: 'Продвинутые навыки и профессиональные возможности.',
    related: 'Офшорное плавание, радиобезопасность.',
    mapDesc: 'Управление более крупными судами, полупрофессиональная квалификация и подготовка к владению судном или работе в отрасли.',
    inSkillMap: true, redTop: false,
  },
  {
    id: 'master10power',
    title: 'Master 10GT/12m Power',
    icon: '/iymcs/icon-compass.svg',
    cardClass: 'navy',
    area: 'Прибрежная навигация / Мотор',
    who: 'Владельцам и шкиперам моторных яхт',
    readiness: 'Опыт управления моторным судном.',
    focus: 'Навигация, управление моторным судном, безопасность.',
    result: 'Сертификат шкипера моторного судна до 12м.',
    related: 'Master 40GT Power, Radio A1',
    inSkillMap: false, redTop: false,
  },
  {
    id: 'master40power',
    title: 'Master 40GT/17m Power',
    icon: '/iymcs/icon-compass.svg',
    cardClass: 'navy',
    area: 'Профессиональное судовождение / Мотор',
    who: 'Опытным капитанам моторных судов',
    readiness: 'Значительный опыт управления моторными судами.',
    focus: 'Расширенная навигация, управление крупным моторным судном.',
    result: 'Сертификат шкипера моторного судна до 17м.',
    related: 'Master 10GT Power',
    inSkillMap: false, redTop: false,
  },
  {
    id: 'racecrew',
    title: 'RaceCrew sail™',
    cardClass: 'red',
    area: 'Гоночный экипаж яхты и безопасность',
    who: 'Члены экипажа и шкиперы, которым нужны гоночные навыки',
    readiness: 'Для выдачи документа могут потребоваться морская практика и подходящая предыдущая сертификация.',
    focus: 'Безопасность гонок, координация экипажа и тактика.',
    result: 'Более безопасный и эффективный участник гоночного экипажа или вахтенный руководитель.',
    related: 'DeckCrew, Master 10, Master 20, радиобезопасность.',
    inSkillMap: false, redTop: false,
  },
  {
    id: 'radio-a1',
    title: 'Radio GMDSS DSC A1 Coastal',
    cardClass: 'red',
    area: 'Прибрежная радиобезопасность',
    who: 'Судоводители в прибрежных водах',
    readiness: 'Полезен вместе с практическими парусными курсами.',
    focus: 'VHF/GMDSS, DSC и процедуры безопасности.',
    result: 'Компетентность в прибрежной радиосвязи.',
    related: 'DeckCrew, прибрежное плавание.',
    mapDesc: 'Основные навыки радиосвязи для прибрежных вод.',
    inSkillMap: true, redTop: true,
  },
  {
    id: 'radio-a1a2',
    title: 'Radio GMDSS DSC A1+A2 Offshore',
    cardClass: 'navy',
    area: 'Офшорная радиобезопасность',
    who: 'Судоводители в офшорных районах',
    readiness: 'Актуален для офшорных и океанских переходов.',
    focus: 'Расширенная морская связь.',
    result: 'Готовность к связи в офшорных районах.',
    related: 'Master 20, Master 40.',
    mapDesc: 'Расширенная морская связь для офшорных и океанских переходов.',
    inSkillMap: true, redTop: false,
  },
  {
    id: 'sportsboat',
    title: 'Open Sportsboat 10m',
    cardClass: 'navy',
    area: 'Спортивные катера / Открытые воды',
    who: 'Владельцам RIB, катеров, спортивных лодок',
    readiness: 'Базовые навыки управления лодкой.',
    focus: 'Безопасность, управление на скорости, навигация.',
    result: 'Сертификат управления спортивными судами до 10м.',
    related: 'PWC, DeckCrew.',
    inSkillMap: false, redTop: false,
  },
  {
    id: 'ocean2040',
    title: 'Celestial Navigation / Ocean 2040',
    cardClass: 'navy',
    area: 'Океанская навигация',
    who: 'Яхтсменам, планирующим дальние переходы',
    readiness: 'Значительный офшорный опыт.',
    focus: 'Астронавигация, океанские маршруты, безопасность в открытом море.',
    result: 'Сертификат астронавигации и океанских переходов.',
    related: 'Master 40GT Sail.',
    inSkillMap: false, redTop: false,
  },
  {
    id: 'pwc',
    title: 'Personal Watercraft (PWC)',
    cardClass: 'navy',
    area: 'Гидроциклы',
    who: 'Владельцам гидроциклов и водных скутеров',
    readiness: 'Отсутствие предварительных требований.',
    focus: 'Безопасность, управление, правила расхождения.',
    result: 'Сертификат оператора PWC iymcs.',
    related: 'Open Sportsboat.',
    inSkillMap: false, redTop: false,
  },
  {
    id: 'deckeng40',
    title: 'Deck Engineer 40GT',
    cardClass: 'navy',
    area: 'Судовая техника / Инжиниринг',
    who: 'Экипажу, отвечающему за техническое обслуживание',
    readiness: 'Технический опыт приветствуется.',
    focus: 'Двигатели, электрика, насосы, аварийные системы.',
    result: 'Сертификат палубного инженера 40GT.',
    related: 'DeckCrew, Master 40GT.',
    inSkillMap: false, redTop: false,
  },
];

const CHOOSER_ITEMS = [
  { art: '/iymcs/graphics/blue-steering-wheel-art.png', q: 'Вы новичок в парусном деле?',                  text: 'Начните с DeckCrew sail™ и заложите практическую основу на воде.',                                                                                           href: 'deckcrew',     accent: false },
  { art: '/iymcs/graphics/compass-art.png',             q: 'Прибрежное плавание?',                          text: 'Master 10GT/12m sail™ — для прибрежных плаваний и ночных переходов.',                                                                                    href: 'master10sail', accent: 'red' },
  { art: '/iymcs/graphics/offshore-wave-art.png',       q: 'Офшорное и глубоководное плавание?',            text: 'Master 20GT/15m sail™ — для более длительных переходов и уверенности в море.',                                                                            href: 'master20sail', accent: false },
  { art: '/iymcs/graphics/globe-art.png',               q: 'Более крупные суда или профессиональные планы?', text: 'Master 40GT/20m sail™ развивает продвинутые навыки и открывает дополнительные профессиональные возможности.',                                            href: 'master40sail', accent: false },
  { art: '/iymcs/graphics/radio-art.png',               q: 'Нужны навыки радиосвязи?',                      text: 'Выберите курс, соответствующий требованиям ITU (Международного союза электросвязи) и вашему району плавания.',                                            href: 'radio-a1',     accent: 'red' },
  { art: '/iymcs/graphics/anchor-blob-art.png',         q: 'У вас уже есть опыт?',                          text: 'Спросите школу, с какого направления подготовки вам лучше начать. При наличии опыта возможен прямой вход после оценки готовности.',                       href: null,           accent: 'experienced' },
];

const IymcsPage = ({ isPreview = false }) => { // eslint-disable-line no-unused-vars
  useSeo({
    title: 'Сертификация IYMCS',
    description: 'Обучение и сертификация по международным стандартам IYMCS в парусной школе The Sailing Doctor. Признанные удостоверения яхтсмена.',
  });
  const { content } = useContext(SiteContentContext);
  const { productionCards } = useContext(CardsContext);
  const navigate = useNavigate();
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);

  const findCourseCard = (iymcsCourseType) =>
    (productionCards?.study || []).find(c =>
      c.iymcsCourseTypes?.includes(iymcsCourseType) ||
      c.iymcsCourseType === iymcsCourseType
    );

  const handleCourseAction = (courseId) => {
    const card = findCourseCard(courseId);
    if (card) {
      const tp = { adventures: 'adventures', study: 'study', races: 'races', main: 'news' };
      navigate(`/${tp[card.type] || 'card'}/${card.slug || card.id}`);
    } else {
      setIsQuestionOpen(true);
    }
  };

  return (
    <>
    <main className="iymcs-page">

      {/* Полоса преимуществ */}
      <section className="iymcs-trust-strip">
        <div className="iymcs-trust-grid">
          {TRUST_ITEMS.map(item => (
            <div key={item.title} className="iymcs-trust-item">
              <span className="iymcs-trust-icon">
                <IIcon src={item.icon} size={32} />
              </span>
              <div>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Зачем iymcs? */}
      <section className="iymcs-section iymcs-why">
        <div className="iymcs-container">
          <h2 className="iymcs-section-title">Почему обучение в системе <IymcsMark />?</h2>
          <div className="iymcs-benefit-grid">
            {BENEFITS.map(b => (
              <div key={b.title} className={`iymcs-benefit-card${b.highlight ? ' iymcs-benefit-card--highlight' : ''}`}>
                <div className="iymcs-benefit-icon">
                  <img src={b.art} alt="" aria-hidden="true" width={80} height={80} style={{ objectFit: 'contain' }} />
                </div>
                <h3>{b.title}</h3>
                {b.text
                  ? <p>{b.text}</p>
                  : <p>В отличие от жёстких ступенчатых систем, <IymcsMark /> позволяет выбирать курсы, которые соответствуют вашим текущим знаниям, морской практике, целям и интересам.</p>
                }
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skill Map + Chooser + Курсы */}
      {(() => {
        const visibleIds = content.iymcsVisibleCourses || [];
        const visibleCourses = ALL_IYMCS_COURSES.filter(c => visibleIds.includes(c.id));
        if (visibleCourses.length === 0) return null;

        const mapTiles = visibleCourses.filter(c => c.inSkillMap);
        const chooserItems = CHOOSER_ITEMS.filter(ci => !ci.href || visibleIds.includes(ci.href));

        return (<>
          {/* Карта навыков */}
          {mapTiles.length > 0 && (
            <section id="skill-map" className="iymcs-section iymcs-skillmap">
              <div className="iymcs-container">
                <h2 className="iymcs-section-title">Постройте свою карту парусных навыков</h2>
                <p className="iymcs-section-kicker">Начинайте с того направления, которое лучше всего соответствует вашему опыту и морской практике. Не обязательно проходить все курсы подряд.</p>
                <div className="iymcs-skillmap-grid">
                  {mapTiles.map((course, idx) => (
                    <div
                      key={course.id}
                      className={`iymcs-skillmap-tile${idx % 2 === 1 ? ' iymcs-skillmap-tile--red' : ''}`}
                      onClick={() => {
                        const el = document.getElementById(course.id);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <h3>{course.title}</h3>
                      <div className="iymcs-skillmap-art">
                        {COURSE_ART[course.id]
                          ? <img src={COURSE_ART[course.id]} alt="" aria-hidden="true" style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
                          : null}
                      </div>
                      <p>{course.mapDesc || course.focus}</p>
                      <span className="iymcs-skillmap-more">Подробнее ↓</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Chooser */}
          {chooserItems.length > 1 && (
            <section className="iymcs-section iymcs-chooser">
              <div className="iymcs-container">
                <h2 className="iymcs-section-title">Какой курс подойдёт мне?</h2>
                <div className="iymcs-chooser-grid">
                  {chooserItems.map(item => (
                    <div
                      key={item.q}
                      className={`iymcs-chooser-item${item.accent === 'red' ? ' iymcs-chooser-item--red' : ''}${item.accent === 'experienced' ? ' iymcs-chooser-item--experienced' : ''}`}
                      onClick={() => {
                        if (item.href) {
                          const el = document.getElementById(item.href);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        } else {
                          setIsQuestionOpen(true);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="iymcs-chooser-icon">
                        <img src={item.art} alt="" aria-hidden="true" width={80} height={80} style={{ objectFit: 'contain' }} />
                      </div>
                      <strong>{item.q}</strong>
                      <p>{item.text}</p>
                      <span className="iymcs-chooser-link">
                        {item.href ? 'Перейти к курсу →' : 'Спросить школу →'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Курсы */}
          <section id="courses" className="iymcs-section iymcs-courses">
            <div className="iymcs-container">
              <h2 className="iymcs-section-title">Посмотреть все курсы</h2>
              <p className="iymcs-section-kicker">Начинайте с того уровня, который подходит вашему опыту. Проходить все курсы подряд не нужно.</p>
              <div className="iymcs-course-grid">
                {visibleCourses.map((course, idx) => (
                  <article key={course.id} id={course.id} className={`iymcs-course-card iymcs-course-card--${idx % 2 === 0 ? 'navy' : 'red'}`}>
                    <div className="iymcs-course-header">
                      <h3>{course.title}</h3>
                    </div>
                    {COURSE_ART[course.id] && (
                      <img className="iymcs-course-art" src={COURSE_ART[course.id]} alt="" aria-hidden="true" />
                    )}
                    <dl className="iymcs-course-dl">
                      <dt>Область компетенции</dt><dd>{course.area}</dd>
                      <dt>Кому подходит</dt><dd>{course.who}</dd>
                      <dt>Готовность</dt><dd>{course.readiness}</dd>
                      <dt>Основной фокус</dt><dd>{course.focus}</dd>
                      <dt>Результат</dt><dd>{course.result}</dd>
                      <dt>Связанные направления</dt><dd>{course.related}</dd>
                    </dl>
                    <button className="iymcs-course-cta" onClick={() => handleCourseAction(course.id)}>
                      {findCourseCard(course.id) ? 'Записаться на курс →' : 'Узнать детали →'}
                    </button>
                  </article>
                ))}
              </div>
              <p className="iymcs-advanced-note">
                Нужен другой курс? Спросите нас или зайдите на{' '}
                <a href={content.iymcsUrl || 'https://iymcs.com'} target="_blank" rel="noopener noreferrer">iymcs.com</a>
                {' '}— полный каталог рекреационных программ.
              </p>
            </div>
          </section>
        </>);
      })()}

      {/* Портал */}
      <section className="iymcs-portal-section">
        <div className="iymcs-container iymcs-portal-inner">
          <div className="iymcs-portal-screenshot">
            <img src="/iymcs/portal-screenshot.jpg" alt="Портал студента iymcs" />
          </div>
          <div className="iymcs-portal-copy">
            <h2>Ваш онлайн-портал студента</h2>
            <p>Все материалы по обучению — в одном месте. Записи, документы и учебные материалы доступны онлайн. Загружайте морскую практику для страховки, сертификации и профессиональных целей.</p>
            <div className="iymcs-portal-features">
              <span><IIcon src="/iymcs/portal-track.svg" size={22} /> Прогресс</span>
              <span><IIcon src="/iymcs/portal-docs.svg" size={22} /> Материалы</span>
              <span><IIcon src="/iymcs/portal-upload.svg" size={22} /> Морская практика</span>
              <span><IIcon src="/iymcs/portal-verify.svg" size={22} /> Проверка и выпуск</span>
            </div>
          </div>
        </div>
      </section>

    </main>

    <section className="cta-section">
      <h2>{withIymcsMark('Хотите получить сертификат iymcs?')}</h2>
      <p>Запишитесь на курс или задайте вопрос — расскажем о программе и условиях. Ответим в течение дня.</p>
      <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
    </section>

    <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="iymcs" />
    </>
  );
};

export default IymcsPage;
